import { createAction, isFulfilled } from '@reduxjs/toolkit'
import findIndex from 'lodash/findIndex'
import get from 'lodash/get'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import { v4 as uuid } from 'uuid'
import { l } from '@manychat/manyui'

import triggerRuleAdapter from 'apps/triggerRules/models/TriggerRule/adapter'
import {
  TriggerRuleFrequencyType,
  TriggerRuleStatusType,
  TriggerRuleType,
} from 'apps/triggerRules/models/TriggerRule/constants'
import {
  getCurrentTriggerRuleItem,
  getTriggerRuleById,
} from 'apps/triggerRules/selectors/triggerRulesSelectors'
import { ITriggerRule } from 'apps/triggerRules/types'
import { TriggerRuleStatusData } from 'apps/triggerRules/types/schemas'
import { getActionListErrors } from 'common/actions/models/Action/validation'
import { alert } from 'common/core'
import { Field } from 'common/core/interfaces/field'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { getCustomEventsItems } from 'common/events/selectors/eventSelectors'
import { getItems } from 'common/fields/selectors/userFieldsSelectors'
import { TriggerType } from 'common/flow/constants/TriggerType'
import { TriggerRuleId, TriggerRule } from 'shared/api/common/schemas/flow/triggerRule'
import { TriggerRulesApi } from 'shared/api/requests/triggerRules'
import { CreateRuleBody } from 'shared/api/requests/triggerRules/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { navigatePollyfill } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import { linkURL } from 'utils/url'

const triggerRuleGuard = (triggerRule: ITriggerRule | null, state: RootState) => {
  if (!triggerRule) {
    return false
  }

  if (!triggerRule.actions?.length) {
    alert(l.translate('At least 1 action must be set'), 'danger')
    return false
  }

  if (!triggerRule.triggers?.length) {
    alert(l.translate('At least 1 trigger must be set'), 'danger')
    return false
  }

  const userFields: Field[] = getItems(state)
  const customEvents: CustomEvent[] = getCustomEventsItems(state)
  const actionErrors = getActionListErrors(triggerRule.actions, { userFields, customEvents })
  if (actionErrors.length) {
    const error = actionErrors[0]
    alert(error.message, 'danger')
    return false
  }

  const invalidTrigger = triggerRule.triggers.find((trigger, index) => {
    if (trigger.type === TriggerRuleType.DATE_TIME_EVENT && !trigger.data?.event_filter?.type) {
      alert(l.translate('Trigger {number}: user field not set', { number: index + 1 }), 'danger')
      return true
    }
    return false
  })

  return !invalidTrigger
}

export const activateRuleWithoutValidation = createAsyncAction(
  'triggerRules/activateRuleWithoutValidation',
  async ({ id }: { id: TriggerRuleId }) => {
    const response = await TriggerRulesApi.changeStatus({
      body: { rule_id: id, status: TriggerRuleStatusType.ACTIVE },
    })

    return response.data.trigger_rule
  },
)

export const fetchTriggerRules = createAsyncAction('triggerRules/fetchTriggerRules', async () => {
  const response = await TriggerRulesApi.list()
  return response.data.trigger_rules.map(triggerRuleAdapter)
})

export const createTriggerRule = createAsyncAction(
  'triggerRules/createTriggerRule',

  async (
    {
      title: rawTitle,
      draft = null,
      triggers = [],
      frequency_settings = TriggerRuleFrequencyType.ONCE,
      ns,
      type,
    }: Partial<CreateRuleBody>,
    { getState },
  ) => {
    const state = getState()
    const { items } = state.triggerRules

    const title = rawTitle || `${l.translate('New Rule')}${items ? ` #${items.length + 1}` : ''}`
    const response = await TriggerRulesApi.create({
      body: {
        title,
        draft,
        triggers,
        frequency_settings,
        ns,
        type,
      },
    })

    return triggerRuleAdapter(response.data.trigger_rule)
  },
)

export const createTriggerRuleAndOpen = createAsyncAction(
  'triggerRules/createTriggerRuleAndOpen',

  async ({ name }: { name: string | undefined }, { dispatch }) => {
    const response = await dispatch(createTriggerRule({ title: name })).unwrap()

    navigatePollyfill(linkURL(`/rules/${response.rule_id}`))

    return response
  },
)

export const getTriggerRule = createAsyncAction(
  'triggerRules/getTriggerRule',

  async ({ id }: { id: TriggerRuleId }) => {
    const response = await TriggerRulesApi.get({ query: { rule_id: id } })

    return response.data
  },
)

export const cloneTriggerRule = createAsyncAction(
  'triggerRules/cloneTriggerRule',

  async ({ id }: { id: TriggerRuleId }) => {
    const response = await TriggerRulesApi.copy({ body: { rule_id: id } })

    return response
  },
)

export const setTriggerRuleStatus = createAsyncAction(
  'triggerRules/setTriggerRuleStatus',

  async ({ id, status }: { id: TriggerRuleId; status: TriggerRuleStatusType }) => {
    const asyncHash = uuid()

    await TriggerRulesApi.changeStatus({
      body: { rule_id: id, status, async: asyncHash },
    })

    analyticsService.sendEvent('TRIGGER.STATUS.SWITCHED', {
      isEnabled: status === TriggerRuleStatusType.ACTIVE,
      triggerId: `${TriggerType.RULE}-${id}`,
    })

    return true
  },
)

export const saveTriggerRule = createAsyncAction(
  'triggerRules/saveTriggerRule',

  async (
    { ruleId, options = {} }: { ruleId: TriggerRuleId; options?: { hideSuccessAlert?: boolean } },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState()
    const triggerRule =
      getCurrentTriggerRuleItem(state, ruleId) || getTriggerRuleById(state, ruleId)

    if (!triggerRule) {
      throw rejectWithValue()
    }

    if (!triggerRuleGuard(triggerRule, state)) throw rejectWithValue()

    const response = await TriggerRulesApi.update({
      body: { rule_id: triggerRule.id, trigger_rule: triggerRule },
    })

    if (response.data.need_activate) {
      await dispatch(
        setTriggerRuleStatus({ id: triggerRule.id, status: TriggerRuleStatusType.ACTIVE }),
      )
    }

    const isPro = getCurrentAccount(getState()).isPro
    const isNotActive =
      getCurrentTriggerRuleItem(state, ruleId)?.status !== TriggerRuleStatusType.ACTIVE

    if (!options.hideSuccessAlert) {
      if (!response.data.need_activate && isNotActive && isPro) {
        alert(l.translate('Rule was successfully saved!'), 'success')
      } else {
        alert(l.translate('Saved!'), 'success')
      }
    }

    return response.data.trigger_rule
  },
  {
    onError(error, { ruleId }, { getState }) {
      if (get(error, 'payload') === undefined) {
        return true
      }

      const state = getState()
      const triggerRule = getCurrentTriggerRuleItem(state, ruleId)

      const triggerErrorMap: Record<string, string[] | string> | string =
        get(error, 'response.data.errors.triggers') || get(error, 'response.data.errors.trigger')

      if (isString(triggerErrorMap)) {
        alert(triggerErrorMap, 'danger')
        return true
      }

      if (isObject(triggerErrorMap)) {
        const triggerId = Object.keys(triggerErrorMap)[0]
        const index = findIndex(triggerRule?.triggers, (trigger) => {
          // eslint-disable-next-line no-underscore-dangle
          return trigger._oid === triggerId || get(trigger, 'data.event_filter._oid') === triggerId
        })
        if (index >= 0) {
          const raw = triggerErrorMap[triggerId]
          const message: string = Array.isArray(raw) ? raw[0] ?? '' : raw ?? ''

          alert(
            l.translate('Trigger {number}: {message}', { number: index + 1, message }),
            'danger',
          )
          return true
        }
      }
      const actions = get(error, 'response.data.errors.actions')
      if (isString(actions)) {
        alert(actions, 'danger')
      }

      return
    },
  },
)

export const setTriggerRuleStatusAndSave = createAsyncAction(
  'triggerRules/setTriggerRuleStatusAndSave',

  async (
    { id, status, source }: { id: TriggerRuleId; status: TriggerRuleStatusType; source: string },
    { dispatch, getState },
  ) => {
    const ruleData = getTriggerRuleById(getState(), id) || getCurrentTriggerRuleItem(getState(), id)

    analyticsService.sendEvent('TRIGGER_RULES.ENABLE.CLICK', { status, source })

    if (ruleData) {
      if (status === TriggerRuleStatusType.ACTIVE) {
        const isSaved = isFulfilled(await dispatch(saveTriggerRule({ ruleId: id })))
        if (!isSaved) {
          return false
        }
      }
    }
    return dispatch(setTriggerRuleStatus({ id, status })).unwrap()
  },
)

export const setTriggerRuleTitle = createAsyncAction(
  'triggerRules/setTriggerRuleTitle',

  async ({ id, title }: { id: TriggerRuleId; title: string }) => {
    const response = await TriggerRulesApi.setTitle({ body: { rule_id: id, title } })
    alert(l.translate(`Renamed!`), 'success')
    return response.data.trigger_rule
  },
)

export const setTriggerRuleStatusNotification = createAction<TriggerRuleStatusData>(
  'triggerRules/setStatusNotification',
)

export const updateTriggerRuleNotification = createAction(
  'triggerRules/updateTriggerRuleNotification',
  (payload: TriggerRule) => ({ payload: triggerRuleAdapter(payload) }),
)

export const triggerRuleTitleSetNotification = createAction(
  'triggerRules/triggerRuleTitleSetNotification',
  (payload: TriggerRule) => ({
    payload: triggerRuleAdapter(payload),
  }),
)

export const deleteTriggerRule = createAsyncAction(
  'triggerRules/deleteTriggerRule',
  async ({ id }: { id: TriggerRuleId }, { getState }) => {
    const state = getState()
    const rule = getTriggerRuleById(state, id) || getCurrentTriggerRuleItem(state, id)

    await TriggerRulesApi.delete({ body: { rule_id: id } })
    if (rule?.title) {
      alert(l.translate('Rule {ruleName} was deleted.', { ruleName: rule.title }), 'info')
    }
    return { id }
  },
)

export const deleteTriggerRuleAndClose = createAsyncAction(
  'triggerRules/deleteTriggerRuleAndClose',
  async ({ id }: { id: TriggerRuleId }, { dispatch }) => {
    await dispatch(deleteTriggerRule({ id }))
    navigatePollyfill(linkURL(`/cms#rules`))
    return true
  },
)

export const createdTriggerRuleNotification = createAction(
  'triggerRules/createdNotification',
  (payload: TriggerRule) => ({
    payload: triggerRuleAdapter(payload),
  }),
)
